import React, { useEffect, useState } from "react";
import Toastify, { notifyError } from "../../../utils/tostfy";
import MasterSuccessModal from "../MasterSuccessModal";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { handleApiCall } from "../../../apis";
import { endpoint } from "../../../utils/endpoints";
import PermissionGate from "../../../Components/PermissionGate/PermissionGate";
import { MODULES } from "../../../utils/permissions";

const AddRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [companyList, setCompanyList] = useState([]);
  const [companyLocations, setCompanyLocations] = useState([]);
  const [miplLocations, setMiplLocations] = useState([]);

  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedCompanyLocation, setSelectedCompanyLocation] = useState("");
  const [selectedMiplLocation, setSelectedMiplLocation] = useState("");
  const [idealAverage, setIdealAverage] = useState("");
  const [singleDriverAmount, setSingleDriverAmount] = useState("");
  const [dualDriverAmount, setDualDriverAmount] = useState("");
  const [editRouteId, setEditRouteId] = useState("");
  const [savingRoute, setSavingRoute] = useState(false);
  const [formErrors, setFormErrors] = useState({
    company_id: "",
    company_location_id: "",
    ideal_average: "",
    mipl_location_id: "",
    single_driver_trip_amount: "",
    dual_driver_trip_amount: "",
  });

  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingCompanyLocations, setLoadingCompanyLocations] = useState(false);
  const [loadingMiplLocations, setLoadingMiplLocations] = useState(true);
  const isEditMode = location?.state?.mode === "edit" && !!editRouteId;
  const [successModal, setSuccessModal] = useState({ open: false, message: "" });
  const openMasterSuccess = (message) =>
    setSuccessModal({ open: true, message: message || "Saved successfully" });
  const closeMasterSuccess = () => setSuccessModal({ open: false, message: "" });

  const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.data)) return value.data;
    return [];
  };

  const mapCompany = (item) => ({
    id: item?._id || item?.id || "",
    name: item?.name || item?.company_name || "Unknown Company",
  });

  const mapLocation = (item) => ({
    id: item?._id || item?.id || "",
    name: item?.name || item?.location_name || "Unknown Location",
  });

  const fetchAllCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await handleApiCall("get", endpoint.listOfAllCompany, {});
      if (response?.data?.code === 200) {
        const d = response?.data?.data;
        // const raw = toArray(d?.company_data) || toArray(d);
        const mapped = d.map(mapCompany).filter((x) => x.id && x.name);
        setCompanyList(mapped);
      } else {
        const errMsg =
          response?.response?.data?.errorMessage ||
          response?.response?.data?.message ||
          response?.data?.message ||
          "Failed to load company list";
        notifyError(errMsg);
      }
    } catch {
      notifyError("Failed to load company list");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchAllMiplLocations = async () => {
    setLoadingMiplLocations(true);
    try {
      const response = await handleApiCall("get", endpoint.listOfAlllocation, {});
      if (response?.data?.code === 200) {
        const d = response?.data?.data;
        // const raw = toArray(d?.location_data) || toArray(d);
        const mapped = d.map(mapLocation).filter((x) => x.id && x.name);
        setMiplLocations(mapped);
      } else {
        const errMsg =
          response?.response?.data?.errorMessage ||
          response?.response?.data?.message ||
          response?.data?.message ||
          "Failed to load MIPL locations";
        notifyError(errMsg);
      }
    } catch {
      notifyError("Failed to load MIPL locations");
    } finally {
      setLoadingMiplLocations(false);
    }
  };

  const fetchCompanyLocationList = async (companyId) => {
    if (!companyId) {
      setCompanyLocations([]);
      setSelectedCompanyLocation("");
      return;
    }
    setLoadingCompanyLocations(true);
    try {
      const response = await handleApiCall(
        "get",
        `${endpoint.listOfCompanyLocation}?company_id=${encodeURIComponent(companyId)}`,
        {}
      );
      if (response?.data?.code === 200) {
        const d = response?.data?.data;
        // const raw = toArray(d?.location_data) || toArray(d);
        const mapped = d.map(mapLocation).filter((x) => x.id && x.name);
        // console.log("listOfCompanyLocation", mapped);
        setCompanyLocations(mapped);
      } else {
        const errMsg =
          response?.response?.data?.errorMessage ||
          response?.response?.data?.message ||
          response?.data?.message ||
          "Failed to load company locations";
        notifyError(errMsg);
      }
    } catch {
      notifyError("Failed to load company locations");
    } finally {
      setLoadingCompanyLocations(false);
    }
  };

  useEffect(() => {
    fetchAllCompanies();
    fetchAllMiplLocations();
  }, []);

  useEffect(() => {
    fetchCompanyLocationList(selectedCompany);
  }, [selectedCompany]);

  useEffect(() => {
    const data = location?.state?.routeData;
    if (!data || location?.state?.mode !== "edit") {
      setEditRouteId("");
      return;
    }
    setEditRouteId(data.id || data.routeId || "");
    setSelectedCompany(data.companyId || "");
    setSelectedCompanyLocation(data.companyLocationId || "");
    setSelectedMiplLocation(data.miplLocationId || "");
    setIdealAverage(`${data.idealAverage ?? ""}`);
    setSingleDriverAmount(
      data.singleDriverTripAmount != null ? `${data.singleDriverTripAmount}` : ""
    );
    setDualDriverAmount(
      data.dualDriverTripAmount != null ? `${data.dualDriverTripAmount}` : ""
    );
    setFormErrors({
      company_id: "",
      company_location_id: "",
      ideal_average: "",
      mipl_location_id: "",
      single_driver_trip_amount: "",
      dual_driver_trip_amount: "",
    });
  }, [location.state]);

  const parseNumber = (value) => {
    if (value == null) return NaN;
    const normalized = `${value}`.replace(/,/g, "").trim();
    if (!normalized) return NaN;
    return Number(normalized);
  };

  const sanitizeDecimalInput = (value, maxDecimals = 2) => {
    let next = `${value}`.replace(/[^\d.]/g, "");
    const dotIdx = next.indexOf(".");
    if (dotIdx !== -1) {
      // Keep first dot only.
      next =
        next.slice(0, dotIdx + 1) +
        next
          .slice(dotIdx + 1)
          .replace(/\./g, "");
      const [intPart, decPart = ""] = next.split(".");
      next = `${intPart}.${decPart.slice(0, maxDecimals)}`;
    }
    return next;
  };

  const sanitizeIntegerInput = (value) => `${value}`.replace(/[^\d]/g, "");

  const validateForm = () => {
    const e = {
      company_id: "",
      company_location_id: "",
      ideal_average: "",
      mipl_location_id: "",
      single_driver_trip_amount: "",
      dual_driver_trip_amount: "",
    };
    if (!selectedCompany) e.company_id = "Company is required";
    if (!selectedCompanyLocation) e.company_location_id = "Company location is required";
    if (!selectedMiplLocation) e.mipl_location_id = "MIPL location is required";

    const ideal = parseNumber(idealAverage);
    const single = parseNumber(singleDriverAmount);
    const dual = parseNumber(dualDriverAmount);

    if (Number.isNaN(ideal) || ideal <= 0) {
      e.ideal_average = "Ideal average must be greater than 0";
    } else if (ideal > 100) {
      e.ideal_average = "Ideal average must be 100 or less";
    } else if (!/^\d+(\.\d{1,2})?$/.test(`${idealAverage}`.trim())) {
      e.ideal_average = "Ideal average can have up to 2 decimal places";
    }

    if (Number.isNaN(single) || single <= 0) {
      e.single_driver_trip_amount = "Single driver amount must be greater than 0";
    } else if (!Number.isInteger(single)) {
      e.single_driver_trip_amount = "Single driver amount must be a whole number";
    }

    if (Number.isNaN(dual) || dual <= 0) {
      e.dual_driver_trip_amount = "Dual driver amount must be greater than 0";
    } else if (!Number.isInteger(dual)) {
      e.dual_driver_trip_amount = "Dual driver amount must be a whole number";
    } else if (!Number.isNaN(single) && Number.isInteger(single) && dual < single) {
      e.dual_driver_trip_amount =
        "Dual driver amount should be greater than or equal to single driver amount";
    }

    return {
      errors: e,
      payload: {
        company_id: selectedCompany,
        company_location_id: selectedCompanyLocation,
        ideal_average: ideal,
        mipl_location_id: selectedMiplLocation,
        single_driver_trip_amount: single,
        dual_driver_trip_amount: dual,
      },
    };
  };

  const handleAddRoute = async () => {
    if (isEditMode && !editRouteId) {
      notifyError("Route id is missing for edit");
      return;
    }
    const { errors, payload } = validateForm();
    setFormErrors(errors);
    if (
      errors.company_id ||
      errors.company_location_id ||
      errors.ideal_average ||
      errors.mipl_location_id ||
      errors.single_driver_trip_amount ||
      errors.dual_driver_trip_amount
    ) {
      return;
    }

    setSavingRoute(true);
    try {
      const requestPayload = isEditMode
        ? { route_id: editRouteId, ...payload }
        : payload;
      const response = await handleApiCall(
        isEditMode ? "put" : "post",
        isEditMode ? endpoint.editRoute : endpoint.addRoute,
        requestPayload
      );
      if (response?.data?.code === 200) {
        openMasterSuccess(
          response?.data?.message ||
            (isEditMode ? "Route updated successfully" : "Route added successfully")
        );
        return;
      }
      const errMsg =
        response?.response?.data?.errorMessage ||
        response?.response?.data?.message ||
        response?.data?.message ||
        (isEditMode ? "Failed to update route" : "Failed to add route");
      notifyError(errMsg);
    } catch {
      notifyError("Something went wrong. Please try again.");
    } finally {
      setSavingRoute(false);
    }
  };

  return (
    <>
    <Toastify/>
      <div className="md:mt-7 mt-3">
        <div className="border md:p-6 p-3 rounded bg-white shadow-sm">
          <div className="font-UbuntuMedium text-lg md:text-xl mb-2">
            {isEditMode ? "Edit Route Details" : "Route Details"}
          </div>
          <div className="grid md:grid-cols-3 md:gap-4 !gap-3 mb-4 font-UbuntuRegular">
            <div className="flex flex-col">
              <select
                className="border p-2 rounded form-control shadow-none"
                value={selectedCompany}
                onChange={(e) => {
                  setSelectedCompany(e.target.value);
                  setSelectedCompanyLocation("");
                  if (formErrors.company_id) {
                    setFormErrors((prev) => ({ ...prev, company_id: "" }));
                  }
                }}
                disabled={loadingCompanies}
              >
                <option value="">
                  {loadingCompanies ? "Loading companies..." : "Select Company"}
                </option>
                {companyList.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
              {formErrors.company_id && (
                <p className="text-danger small mt-1 mb-0">{formErrors.company_id}</p>
              )}
            </div>

            <div className="flex flex-col">
              <select
                className="border p-2 rounded form-control shadow-none"
                value={selectedCompanyLocation}
                onChange={(e) => {
                  setSelectedCompanyLocation(e.target.value);
                  if (formErrors.company_location_id) {
                    setFormErrors((prev) => ({ ...prev, company_location_id: "" }));
                  }
                }}
                disabled={!selectedCompany || loadingCompanyLocations}
              >
                <option value="">
                  {!selectedCompany
                    ? "Select Company First"
                    : loadingCompanyLocations
                    ? "Loading company locations..."
                    : "Select Company Location"}
                </option>
                {companyLocations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
              {formErrors.company_location_id && (
                <p className="text-danger small mt-1 mb-0">
                  {formErrors.company_location_id}
                </p>
              )}
            </div>

            <div className="flex flex-col">
              <input
                type="text"
                placeholder="Enter Ideal Average"
                className="border p-2 rounded form-control shadow-none"
                value={idealAverage}
                onChange={(e) => {
                  setIdealAverage(sanitizeDecimalInput(e.target.value, 2));
                  if (formErrors.ideal_average) {
                    setFormErrors((prev) => ({ ...prev, ideal_average: "" }));
                  }
                }}
                inputMode="decimal"
              />
              {formErrors.ideal_average && (
                <p className="text-danger small mt-1 mb-0">{formErrors.ideal_average}</p>
              )}
            </div>

            <div className="flex flex-col">
              <select
                className="border p-2 rounded col-span-1 form-control shadow-none"
                value={selectedMiplLocation}
                onChange={(e) => {
                  setSelectedMiplLocation(e.target.value);
                  if (formErrors.mipl_location_id) {
                    setFormErrors((prev) => ({ ...prev, mipl_location_id: "" }));
                  }
                }}
                disabled={loadingMiplLocations}
              >
                <option value="">
                  {loadingMiplLocations
                    ? "Loading MIPL locations..."
                    : "Select MIPL Location"}
                </option>
                {miplLocations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
              {formErrors.mipl_location_id && (
                <p className="text-danger small mt-1 mb-0">{formErrors.mipl_location_id}</p>
              )}
            </div>
          </div>
          <div>
            <h3 className="font-UbuntuMedium mb-2">Trip Amount</h3>
            <div className="flex gap-6 font-UbuntuMedium">
              <div className="md:flex items-center gap-2">
                <span className="max-[767px]:text-sm max-[767px]:font-UbuntuBold">Single Driver ₹</span>
                <input
                  type="text"
                  value={singleDriverAmount}
                  maxLength={5}
                  onChange={(e) => {
                    setSingleDriverAmount(sanitizeIntegerInput(e.target.value));
                    if (formErrors.single_driver_trip_amount) {
                      setFormErrors((prev) => ({ ...prev, single_driver_trip_amount: "" }));
                    }
                    if (formErrors.dual_driver_trip_amount) {
                      setFormErrors((prev) => ({ ...prev, dual_driver_trip_amount: "" }));
                    }
                  }}
                  className="border px-3 py-1 rounded w-full md:w-28 h-12 font-UbuntuRegular"
                  inputMode="numeric"
                />
              </div>
              <div className="md:flex items-center gap-2">
              <span className="max-[767px]:text-sm max-[767px]:font-UbuntuBold">Dual Driver ₹</span>
                <input
                  type="text"
                  value={dualDriverAmount}
                  maxLength={5}
                  onChange={(e) => {
                    setDualDriverAmount(sanitizeIntegerInput(e.target.value));
                    if (formErrors.dual_driver_trip_amount) {
                      setFormErrors((prev) => ({ ...prev, dual_driver_trip_amount: "" }));
                    }
                  }}
                  className="border px-3 py-1 rounded w-full md:w-28 h-12 font-UbuntuRegular"
                  inputMode="numeric"
                />
              </div>
            </div>
            <div className="flex gap-6 mt-1">
              <div className="w-[152px]">
                {formErrors.single_driver_trip_amount && (
                  <p className="text-danger small mb-0">{formErrors.single_driver_trip_amount}</p>
                )}
              </div>
              <div className="w-[152px]">
                {formErrors.dual_driver_trip_amount && (
                  <p className="text-danger small mb-0">{formErrors.dual_driver_trip_amount}</p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-x-3 items-center md:mt-4 mt-3 col-md-12 font-UbuntuRegular">
            <Link to="/route-master" className='btn btn-primary max-[767px]:!py-2'>Cancel</Link>
            <PermissionGate module={MODULES.MASTER} action={isEditMode ? "edit" : "add"}>
              <button
                type="button"
                className="btn btn-danger min-w-32 max-[767px]:!py-2"
                onClick={handleAddRoute}
                disabled={savingRoute}
              >
                {savingRoute
                  ? isEditMode
                    ? "Saving..."
                    : "Adding..."
                  : isEditMode
                  ? "Save"
                  : "Add"}
              </button>
            </PermissionGate>
        </div>
      </div>
      <MasterSuccessModal
        open={successModal.open}
        message={successModal.message}
        onClose={() => {
          closeMasterSuccess();
          navigate("/route-master");
        }}
      />
    </>
  );
};

export default AddRoute;
